import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import { ArrowLeftIcon } from '@hero/web-icons';
import { Button } from '../../ui/Button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../ui/Sheet/Sheet';

export interface EditDrawerStep {
  /** Stable id, used by `currentStepId`. */
  id: string;
  /** Step title (replaces `title` when this step is active). */
  title?: string;
  /** Step description (replaces `description` when active). */
  description?: string;
  /** Body for this step. */
  content: React.ReactNode;
  /**
   * Label for the primary "next" / "submit" button on this step.
   * Defaults to "Continue" on non-terminal steps and "Save" on the
   * last step. The last step is detected by index.
   */
  primaryLabel?: string;
  /**
   * Callback fired when the user clicks the primary button on this
   * step. Return `false` to keep the drawer on this step (e.g. when
   * validation fails). Defaults to advancing to the next step.
   *
   * The terminal step typically performs the actual save inside
   * this callback — pair with `isSubmitting` on the drawer to
   * surface a spinner.
   */
  onPrimary?: () => void | boolean | Promise<void | boolean>;
  /**
   * Disable the primary button on this step. Use for "select at
   * least one row" type guards.
   */
  primaryDisabled?: boolean;
  /** Hide the back button on this step (defaults to false on step 0). */
  hideBack?: boolean;
}

export interface EditDrawerProps {
  /** Drawer visibility — wire to local state or a Zustand store. */
  isOpen: boolean;
  /** Close handler — called on `Sheet`'s `onOpenChange(false)` or the X button. */
  onClose: () => void;

  /** Header title shown above the body. Steps can override. */
  title: string;
  /** Optional subtitle / helper copy under the title. Steps can override. */
  description?: React.ReactNode;

  /**
   * Two-step+ flow: when set, the drawer renders an internal
   * stepper and the primary button advances through `steps` in
   * order. Mutually exclusive with `children` (children take
   * precedence when both are set).
   */
  steps?: EditDrawerStep[];
  /** Currently-active step id. Required when `steps` is set. */
  currentStepId?: string;
  /** Step navigation handler. Required when `steps` is set. */
  onStepChange?: (stepId: string) => void;

  /**
   * Single-step body — used when no `steps` are provided. The
   * caller renders its own form/content; the drawer just hosts
   * the header + sticky footer.
   */
  children?: React.ReactNode;

  /**
   * Single-step primary action callback. Required when `children`
   * is set and `steps` is not.
   */
  onSubmit?: () => void | Promise<void>;
  /** Disable the primary button (single-step mode). */
  submitDisabled?: boolean;
  /** Primary button label (single-step mode). Defaults to "Save". */
  submitLabel?: string;

  /**
   * Renders the primary button in a "loading" state with a
   * loading label. Use during the save mutation. Applies to both
   * single-step and multi-step modes.
   */
  isSubmitting?: boolean;
  /** Override the loading label. Defaults to "Saving…". */
  loadingLabel?: string;
  /** Override the cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;

  /**
   * Tailwind classes for the `SheetContent`. Defaults to the
   * standard right-side drawer width (`sm:max-w-xl`).
   */
  contentClassName?: string;
}

/**
 * `EditDrawer` — Shared scaffold for "create / edit X" drawers.
 *
 * Wraps the `Sheet` + sticky header + scrollable body + sticky
 * footer pattern that every form drawer in the dashboards uses
 * (`AgreementEditDrawer`, etc.) into a single component so new
 * features don't re-roll the chrome each time.
 *
 * Two modes:
 *
 * 1. **Single-step** — pass `children` + `onSubmit`. The footer
 *    renders Cancel + a single primary button.
 * 2. **Multi-step** — pass `steps[]` + `currentStepId` +
 *    `onStepChange`. The footer renders Back (after step 0) +
 *    a primary button that advances by default. The terminal
 *    step's `onPrimary` typically performs the save.
 *
 * Both modes share the same chrome — there's only one place to
 * change drawer paddings, sticky borders, button alignment, etc.
 *
 * @example single-step
 * ```tsx
 * <EditDrawer
 *   isOpen={open}
 *   onClose={() => setOpen(false)}
 *   title="Edit thing"
 *   onSubmit={handleSave}
 *   submitDisabled={!isValid}
 *   isSubmitting={mutation.isPending}>
 *   <MyForm />
 * </EditDrawer>
 * ```
 *
 * @example multi-step
 * ```tsx
 * const [step, setStep] = useState<'select' | 'details'>('select');
 * <EditDrawer
 *   isOpen={open}
 *   onClose={() => setOpen(false)}
 *   title="Create invoice"
 *   steps={[
 *     { id: 'select', content: <SelectStep />, primaryDisabled: !count, primaryLabel: 'Continue' },
 *     { id: 'details', content: <DetailsStep />, onPrimary: handleCreate, primaryLabel: 'Create' },
 *   ]}
 *   currentStepId={step}
 *   onStepChange={setStep}
 *   isSubmitting={mutation.isPending}
 * />
 * ```
 */
export function EditDrawer({
  isOpen,
  onClose,
  title,
  description,
  steps,
  currentStepId,
  onStepChange,
  children,
  onSubmit,
  submitDisabled = false,
  submitLabel,
  isSubmitting = false,
  loadingLabel,
  cancelLabel,
  contentClassName,
}: EditDrawerProps) {
  const { t } = useTranslation('c-main');

  const hasSteps = !!steps && steps.length > 0 && !children;
  const stepIndex = hasSteps && currentStepId
    ? steps!.findIndex(s => s.id === currentStepId)
    : -1;
  const activeStep = hasSteps && stepIndex >= 0 ? steps![stepIndex] : undefined;
  const isLastStep = hasSteps && stepIndex === steps!.length - 1;

  const headerTitle = activeStep?.title ?? title;
  const headerDescription = activeStep?.description ?? description;

  const defaultPrimaryLabel = useMemo(() => {
    if (hasSteps) {
      return isLastStep
        ? t('crud_buttons.save', { defaultValue: 'Save' })
        : t('crud_buttons.continue', { defaultValue: 'Continue' });
    }
    return submitLabel ?? t('crud_buttons.save', { defaultValue: 'Save' });
  }, [hasSteps, isLastStep, submitLabel, t]);

  const primaryLabel =
    activeStep?.primaryLabel ?? submitLabel ?? defaultPrimaryLabel;
  const primaryDisabled =
    (activeStep?.primaryDisabled ?? submitDisabled) || isSubmitting;
  const canGoBack =
    hasSteps && stepIndex > 0 && !activeStep?.hideBack;

  const handlePrimary = async () => {
    if (isSubmitting) return;
    if (hasSteps && activeStep) {
      const res = await activeStep.onPrimary?.();
      // If the step doesn't declare an onPrimary handler and isn't
      // the last step, default behaviour is "advance to next step".
      if (!activeStep.onPrimary && !isLastStep) {
        const next = steps![stepIndex + 1];
        onStepChange?.(next.id);
        return;
      }
      // Explicit `false` from a step's onPrimary means "stay" (e.g.
      // validation failed). Anything else (undefined/true) means
      // advance — unless we're already at the last step.
      if (res !== false && !isLastStep) {
        const next = steps![stepIndex + 1];
        onStepChange?.(next.id);
      }
    } else {
      await onSubmit?.();
    }
  };

  const handleBack = () => {
    if (!canGoBack) return;
    const prev = steps![stepIndex - 1];
    onStepChange?.(prev.id);
  };

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className={cn(
          'flex w-full flex-col overflow-y-auto p-0 sm:max-w-xl',
          contentClassName,
        )}>
        <SheetHeader className="sticky top-0 z-10 border-b bg-white px-6 py-4">
          <SheetTitle className="text-base font-semibold">{headerTitle}</SheetTitle>
          <SheetDescription
            className={headerDescription ? 'text-sm text-muted-foreground' : 'sr-only'}>
            {headerDescription ?? ''}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 p-6">
          {hasSteps ? activeStep?.content : children}
        </div>

        <div className="sticky bottom-0 z-10 flex items-center justify-between gap-2 border-t bg-white px-6 py-3">
          {canGoBack ? (
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2">
              <ArrowLeftIcon className="size-4 rtl:rotate-180" />
              {t('crud_buttons.back', { defaultValue: 'Back' })}
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
              {cancelLabel ?? t('crud_buttons.cancel', { defaultValue: 'Cancel' })}
            </Button>
          )}

          <Button onClick={handlePrimary} disabled={primaryDisabled}>
            {isSubmitting
              ? (loadingLabel ??
                t('async_operations.saving', { defaultValue: 'Saving…' }))
              : primaryLabel}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
